import React from 'react'
import PropTypes from 'prop-types'
import {Box, Flex, Img, Text, useStyleConfig} from '@chakra-ui/react'
import {Navigation, Keyboard} from 'swiper/modules'
import {Swiper, SwiperSlide} from 'swiper/react'
import {ChevronRight, ChevronLeft} from '../../../icons'
import {Link} from 'react-router-dom'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {useProductSearch} from '@salesforce/commerce-sdk-react'
import CurrentPrice from '../../../display-price/current-price'
import {getCategoryData, getGlobalLink} from '../../../../utils/contentstack-utils'
import {useGetCategoriesData} from '../../../../custom-api/hooks/use-get-categories-data'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useBooleanCustomPreference} from '../../../../hooks/use-custom-preference'

const ProductCatalogSlider = ({categorySfcc}) => {
    const swiperStyles = useStyleConfig('Swiper')
    const styles = useStyleConfig('ShopTheLook')

    const {currency: activeCurrency} = useCurrency()
    const categoriesData = useGetCategoriesData()
    const {categoryId} = getCategoryData(categorySfcc, categoriesData)
    const isNotShoppableCountry = useBooleanCustomPreference('notShoppableCountry')

    const refine = [
        `cgid=${categoryId ?? ''}`,
        'c_skuEnabledForCountry=true',
        'c_skuIsPDPOnly=false',
        'c_visibleByAll=true',
    ]

    if (!isNotShoppableCountry) refine.push('price=(0.01..9999999)')

    const {site, locale} = useMultiSite()
    const {data} = useProductSearch(
        {
            parameters: {
                q: '',
                refine,
                expand: ['represented_products', 'page_meta_tags'],
                perPricebook: true,
                allVariationProperties: true,
                locale: locale.id,
                site: site.id,
                currency: activeCurrency,
                allImages: false,
            },
        },
        {
            enabled: true,
            keepPreviousData: true,
        },
    )

    return (
        <Box
            __css={{...swiperStyles, ...styles.slider}}
            data-testid="shop-the-look-product-catalog-slider"
        >
            {data?.hits?.length > 0 && (
                <Swiper
                    key={{base: 'mobile-view', md: 'other-view'}}
                    modules={[Navigation, Keyboard]}
                    navigation={{
                        prevEl: `.stl-swiper-button-prev`,
                        nextEl: `.stl-swiper-button-next`,
                    }}
                    keyboard={{
                        enabled: true,
                    }}
                    loop={true}
                    enabled={true}
                    centeredSlides={true}
                    className={'stp-swiper'}
                >
                    {data?.hits?.map((tile, tIndex) => {
                        const {url, label} = getGlobalLink({
                            link: {productsfcc: {data: [{id: tile.productId || ''}]}},
                            productData: data,
                            locale: locale.alias,
                        })

                        return (
                            <SwiperSlide key={`shop-the-look-tile-${tIndex}`}>
                                <Flex
                                    as={Link}
                                    to={url}
                                    title={label}
                                    direction={'column'}
                                    {...styles.tile}
                                >
                                    <Flex direction={'column'} {...styles.tileElem}>
                                        {tile.c_image?.url && (
                                            <Img
                                                src={tile.c_image?.url}
                                                alt={tile.c_image?.alt || ''}
                                                {...styles.productImage}
                                            />
                                        )}
                                        {tile.productId && (
                                            <Text
                                                {...styles.productId}
                                                textStyle={'body-xsmall'}
                                                fontWeight={'normal'}
                                            >
                                                {tile.productId}
                                            </Text>
                                        )}
                                        {tile.productName && (
                                            <Text
                                                {...styles.productName}
                                                textStyle={'body-large'}
                                                fontWeight={'bold'}
                                            >
                                                {tile.productName}
                                            </Text>
                                        )}
                                    </Flex>
                                    <Flex direction={'column'} {...styles.tileElem}>
                                        {tile.price && (
                                            <CurrentPrice
                                                price={tile.price}
                                                currency={tile.currency || activeCurrency}
                                                color={'text.default'}
                                                textStyle={'body-large'}
                                                fontWeight={'normal'}
                                            />
                                        )}
                                        <Flex direction={'row'}>
                                            {tile.c_omnibusPrice?.omnibusValue && (
                                                <CurrentPrice
                                                    price={parseFloat(
                                                        tile.c_omnibusPrice?.omnibusValue,
                                                    )}
                                                    currency={tile.currency || activeCurrency}
                                                    {...styles.omnibusPrice}
                                                    textStyle={'body-xsmall'}
                                                    fontWeight={'normal'}
                                                />
                                            )}
                                            {tile.c_omnibusPrice?.omnibusPercentage && (
                                                <Text
                                                    {...styles.omnibusPercentage}
                                                    textStyle={'body-xsmall'}
                                                    fontWeight={'normal'}
                                                >
                                                    {tile.c_omnibusPrice?.omnibusPercentage || ''}
                                                </Text>
                                            )}
                                        </Flex>
                                    </Flex>
                                </Flex>
                            </SwiperSlide>
                        )
                    })}
                    <ChevronLeft
                        className="stl-swiper-button-prev"
                        aria-label="swiper button prev icon"
                        {...styles.chevronIcon}
                        left={0}
                    />
                    <ChevronRight
                        className="stl-swiper-button-next"
                        aria-label="swiper button next icon"
                        {...styles.chevronIcon}
                        right={0}
                    />
                </Swiper>
            )}
        </Box>
    )
}

ProductCatalogSlider.propTypes = {
    categorySfcc: PropTypes.shape(),
}

export default ProductCatalogSlider
